/**
 * useAccountTree
 *
 * Manages account data fetching, tree structure, expansion state,
 * filtering/sorting and derived row data.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'

import {
  buildRowsByType,
  linkOrphanBankAccounts,
  mapLocalAccounts,
} from './account-tree-utils'
import { ACCOUNT_TYPES, sortIdsByCode, type AccountRow } from './chart-of-accounts-helpers'
import { useToast } from '../../components/ui'
import { useBaseCurrency } from '../../hooks/use-currencies'
import { listAccounts, type Account, type AccountType } from '../../services/account-service'
import { getLocalAccounts } from '../../services/local-db'
import { useLedgerContext } from '../../stores/ledger-store'
import { useOrganization } from '../../stores/organization-store'

import type { FilterBarCondition } from '@elsapiens/ui'


interface ProcessedAccounts {
  accountsMap: Map<string, Account>
  rootAccountsByType: Map<AccountType, string[]>
  childrenMap: Map<string, string[]>
}

function buildAccountMaps(accounts: Account[]): ProcessedAccounts {
  const accountsMap = new Map<string, Account>()
  const rootAccountsByType = new Map<AccountType, string[]>()
  const childrenMap = new Map<string, string[]>()
  const seen = new Set<string>()

  accounts.filter(a => { if (seen.has(a.code)) { return false }; seen.add(a.code); return true })
    .forEach(account => {
      accountsMap.set(account.id, account)
      if (!account.parentId) {
        const roots = rootAccountsByType.get(account.accountType) ?? []
        roots.push(account.id)
        rootAccountsByType.set(account.accountType, roots)
      } else {
        const children = childrenMap.get(account.parentId) ?? []
        children.push(account.id)
        childrenMap.set(account.parentId, children)
      }
      if (account.isHeader && !childrenMap.has(account.id)) {
        childrenMap.set(account.id, [])
      }
    })

  const getCode = (id: string): string => accountsMap.get(id)?.code ?? ''
  rootAccountsByType.forEach((ids, type) => { rootAccountsByType.set(type, sortIdsByCode(ids, getCode)) })
  childrenMap.forEach((ids, parentId) => { childrenMap.set(parentId, sortIdsByCode(ids, getCode)) })

  return { accountsMap, rootAccountsByType, childrenMap }
}

export interface UseAccountTreeReturn {
  accountsMap: Map<string, Account>
  setAccountsMap: React.Dispatch<React.SetStateAction<Map<string, Account>>>
  rootAccountsByType: Map<AccountType, string[]>
  setRootAccountsByType: React.Dispatch<React.SetStateAction<Map<AccountType, string[]>>>
  childrenMap: Map<string, string[]>
  setChildrenMap: React.Dispatch<React.SetStateAction<Map<string, string[]>>>
  rowsByType: Map<AccountType, AccountRow[]>
  isLoading: boolean
  loadingChildren: Set<string>
  expandedTypes: Set<string>
  expandedAccounts: Set<string>
  toggleTypeExpanded: (type: string) => void
  toggleAccountExpanded: (accountId: string) => Promise<void>
  fetchRootAccounts: () => Promise<void>
  baseCurrency: string
  organizationId: string
  currentLedgerId: string | null
  /**
   * Root-fetch pagination status (from the backend's "peek 3 pages ahead"
   * window on ListAccounts). This is a hierarchical, load-in-full tree —
   * not a flat page — so there is no Prev/Next chrome here, only a status
   * caption (see CursorRangeText usage in ChartOfAccountsPage). See design
   * spec: COA gets CursorRangeText only, no CursorRangePagination row.
   */
  loadedCount: number
  remainingCount: number
  remainingIsExact: boolean
}

interface AccountTreeState {
  accountsMap: Map<string, Account>
  setAccountsMap: React.Dispatch<React.SetStateAction<Map<string, Account>>>
  rootAccountsByType: Map<AccountType, string[]>
  setRootAccountsByType: React.Dispatch<React.SetStateAction<Map<AccountType, string[]>>>
  childrenMap: Map<string, string[]>
  setChildrenMap: React.Dispatch<React.SetStateAction<Map<string, string[]>>>
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  expandedTypes: Set<string>
  setExpandedTypes: React.Dispatch<React.SetStateAction<Set<string>>>
  expandedAccounts: Set<string>
  setExpandedAccounts: React.Dispatch<React.SetStateAction<Set<string>>>
  loadingChildren: Set<string>
  setLoadingChildren: React.Dispatch<React.SetStateAction<Set<string>>>
  loadingRef: React.MutableRefObject<boolean>
  orphanCleanupDone: React.MutableRefObject<boolean>
  loadedCount: number
  setLoadedCount: React.Dispatch<React.SetStateAction<number>>
  remainingCount: number
  setRemainingCount: React.Dispatch<React.SetStateAction<number>>
  remainingIsExact: boolean
  setRemainingIsExact: React.Dispatch<React.SetStateAction<boolean>>
}

function useAccountTreeState(): AccountTreeState {
  const [accountsMap, setAccountsMap] = useState<Map<string, Account>>(new Map())
  const [rootAccountsByType, setRootAccountsByType] = useState<Map<AccountType, string[]>>(new Map())
  const [childrenMap, setChildrenMap] = useState<Map<string, string[]>>(new Map())
  const [isLoading, setIsLoading] = useState(true)
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set(ACCOUNT_TYPES))
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set())
  const [loadingChildren, setLoadingChildren] = useState<Set<string>>(new Set())
  const loadingRef = useRef(false)
  const orphanCleanupDone = useRef(false)
  // Root-fetch "peek 3 pages ahead" status — see UseAccountTreeReturn doc comment.
  const [loadedCount, setLoadedCount] = useState(0)
  const [remainingCount, setRemainingCount] = useState(0)
  const [isRemainingExact, setRemainingIsExact] = useState(true)
  return {
    accountsMap, setAccountsMap,
    rootAccountsByType, setRootAccountsByType,
    childrenMap, setChildrenMap,
    isLoading, setIsLoading,
    expandedTypes, setExpandedTypes,
    expandedAccounts, setExpandedAccounts,
    loadingChildren, setLoadingChildren,
    loadingRef, orphanCleanupDone,
    loadedCount, setLoadedCount,
    remainingCount, setRemainingCount,
    remainingIsExact: isRemainingExact, setRemainingIsExact,
  }
}

function useFetchRootAccounts(
  s: AccountTreeState,
  organizationId: string,
  addToast: ReturnType<typeof useToast>['addToast'],
): () => Promise<void> {
  const processAccounts = useCallback((accounts: Account[]): void => {
    const processed = buildAccountMaps(accounts)
    s.setAccountsMap(processed.accountsMap)
    s.setRootAccountsByType(processed.rootAccountsByType)
    s.setChildrenMap(processed.childrenMap)
  }, [s])

  return useCallback(async (): Promise<void> => {
    if (s.loadingRef.current) { return }
    s.loadingRef.current = true
    s.setIsLoading(true)
    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500))
    try {
      const response = await listAccounts(organizationId, { includeHeaders: true, includeInactive: true }, 1000)
      if (response.data && response.data.accounts.length > 0) {
        await minLoadingTime
        processAccounts(response.data.accounts)
        s.setLoadedCount(response.data.accounts.length)
        s.setRemainingCount(response.data.pageInfo.remainingCount ?? 0)
        s.setRemainingIsExact(response.data.pageInfo.remainingIsExact ?? true)
        s.setIsLoading(false)
        s.loadingRef.current = false
        return
      }
    } catch { /* fall through to local-db */ }
    try {
      const local = await getLocalAccounts(organizationId, undefined)
      const mapped = mapLocalAccounts(local)
      await minLoadingTime
      processAccounts(mapped)
      // Local fallback has no server-side "peek ahead" — treat it as complete.
      s.setLoadedCount(mapped.length)
      s.setRemainingCount(0)
      s.setRemainingIsExact(true)
    } catch {
      await minLoadingTime
      addToast({ variant: 'error', message: 'Failed to load accounts. Please try again later.' })
    } finally {
      s.setIsLoading(false)
      s.loadingRef.current = false
    }
  }, [organizationId, addToast, processAccounts, s])
}

function useLoadChildren(s: AccountTreeState, organizationId: string): (parentId: string) => Promise<void> {
  return useCallback(async (parentId: string): Promise<void> => {
    if (s.childrenMap.has(parentId)) { return }
    s.setLoadingChildren(prev => new Set(prev).add(parentId))
    try {
      const response = await listAccounts(organizationId, { parentId, includeHeaders: true, includeInactive: true }, 1000)
      if (response.data?.accounts && response.data.accounts.length > 0) {
        const newMap = new Map(s.accountsMap)
        const childIds: string[] = []
        response.data.accounts.forEach(a => { newMap.set(a.id, a); childIds.push(a.id) })
        const getCode = (id: string): string => newMap.get(id)?.code ?? ''
        s.setAccountsMap(newMap)
        s.setChildrenMap(new Map(s.childrenMap).set(parentId, sortIdsByCode(childIds, getCode)))
      } else {
        s.setChildrenMap(new Map(s.childrenMap).set(parentId, []))
      }
    } catch {
      s.setChildrenMap(new Map(s.childrenMap).set(parentId, []))
    } finally {
      s.setLoadingChildren(prev => { const n = new Set(prev); n.delete(parentId); return n })
    }
  }, [s, organizationId])
}

function useToggleHandlers(
  s: AccountTreeState,
  loadChildren: (parentId: string) => Promise<void>,
): { toggleTypeExpanded: (type: string) => void; toggleAccountExpanded: (accountId: string) => Promise<void> } {
  const toggleTypeExpanded = useCallback((type: string): void => {
    s.setExpandedTypes(prev => {
      const next = new Set(prev)
      if (next.has(type)) { next.delete(type) } else { next.add(type) }
      return next
    })
  }, [s])

  const toggleAccountExpanded = useCallback(async (accountId: string): Promise<void> => {
    const account = s.accountsMap.get(accountId)
    if (!account?.isHeader) { return }
    if (s.expandedAccounts.has(accountId)) {
      s.setExpandedAccounts(prev => { const next = new Set(prev); next.delete(accountId); return next })
    } else {
      s.setExpandedAccounts(prev => new Set(prev).add(accountId))
      if (!s.childrenMap.has(accountId)) { await loadChildren(accountId) }
    }
  }, [s, loadChildren])

  return { toggleTypeExpanded, toggleAccountExpanded }
}

export function useAccountTree(
  searchQuery: string,
  filterConditions: FilterBarCondition[],
  sortField: string,
  sortDirection: 'asc' | 'desc',
): UseAccountTreeReturn {
  const { addToast } = useToast()
  const { organizationId } = useOrganization()
  const { currentLedgerId, isLoaded: isLedgerLoaded } = useLedgerContext()
  const baseCurrency = useBaseCurrency()
  const s = useAccountTreeState()
  const fetchRootAccounts = useFetchRootAccounts(s, organizationId, addToast)
  const loadChildren = useLoadChildren(s, organizationId)
  const { toggleTypeExpanded, toggleAccountExpanded } = useToggleHandlers(s, loadChildren)

  useEffect(() => {
    if (!isLedgerLoaded) { return }
    void fetchRootAccounts()
    // fetchRootAccounts is intentionally excluded: it is recreated every render
    // because useAccountTreeState() returns a fresh object literal. Including
    // it in the dep array causes an infinite fetch loop — every successful
    // fetch triggers state updates, which re-create the callback, which fires
    // this effect again. The callback always reads the latest setters/refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLedgerId, organizationId, isLedgerLoaded])

  useEffect(() => {
    if (s.isLoading || s.orphanCleanupDone.current || !currentLedgerId || s.accountsMap.size === 0) { return }
    s.orphanCleanupDone.current = true
    void linkOrphanBankAccounts(organizationId, currentLedgerId, s.accountsMap)
  }, [s.isLoading, currentLedgerId, s.accountsMap, organizationId, s.orphanCleanupDone])

  const rowsByType = useMemo(() => buildRowsByType({
    accountsMap: s.accountsMap, rootAccountsByType: s.rootAccountsByType,
    childrenMap: s.childrenMap, expandedAccounts: s.expandedAccounts,
    loadingChildren: s.loadingChildren, searchQuery, filterConditions,
    sortField, sortDirection, baseCurrency,
  }), [s.accountsMap, s.rootAccountsByType, s.childrenMap, s.expandedAccounts,
    s.loadingChildren, searchQuery, filterConditions, sortField, sortDirection, baseCurrency])

  return {
    accountsMap: s.accountsMap, setAccountsMap: s.setAccountsMap,
    rootAccountsByType: s.rootAccountsByType, setRootAccountsByType: s.setRootAccountsByType,
    childrenMap: s.childrenMap, setChildrenMap: s.setChildrenMap,
    rowsByType, isLoading: s.isLoading, loadingChildren: s.loadingChildren,
    expandedTypes: s.expandedTypes, expandedAccounts: s.expandedAccounts,
    toggleTypeExpanded, toggleAccountExpanded,
    fetchRootAccounts, baseCurrency, organizationId, currentLedgerId,
    loadedCount: s.loadedCount, remainingCount: s.remainingCount, remainingIsExact: s.remainingIsExact,
  }
}
